import React, { useEffect, useState } from "react";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import { useTranslation } from "react-i18next";
import Title from "components/Title";
import Button from "components/Button";
import { Code, UnclaimedCodes } from "model";
import api from "../../api";
import { tryCatch } from "../../utils";

type UnclaimedCodesDict = {
  [type: string]: string[];
};

const CodesContainer: React.FC<{}> = () => {
  const { t } = useTranslation("codes");
  const [codes, setCodes] = useState<Code[]>([]);
  const [unclaimedCodes, setUnclaimedCodes] = useState<UnclaimedCodesDict>({});

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "codes" });
    getNewCodeList();
  }, []);

  const getNewCodeList = async () => {
    let codeListRes = await tryCatch(api.subscription.getCodesList());
    if (codeListRes.error) {
      console.log("Error fetching codes: ", codeListRes.error);
    } else {
      if (codeListRes.result && codeListRes.result.data) {
        if (codeListRes.result.data.codes) {
          let displayedCodes: Code[] = [];
          codeListRes.result.data.codes.forEach((code) => {
            if (code.type.includes("NordVPN")) {
              displayedCodes.push(code);
            }
          });
          setCodes(displayedCodes);
        }

        if (codeListRes.result.data.unclaimed_codes) {
          let unclaimedCodes: UnclaimedCodesDict = {};
          codeListRes.result.data.unclaimed_codes.forEach((unclaimed_codes) => {
            unclaimed_codes.code_types.forEach((code_type) => {
              if (!unclaimedCodes[code_type]) {
                unclaimedCodes[code_type] = [];
              }
              unclaimedCodes[code_type].push(unclaimed_codes.adapter_id);
            });
          });
          setUnclaimedCodes(unclaimedCodes);
        }
      }
    }
  };

  const handleClaimCode = async (adapter_id: string, type: string) => {
    const apiResult = await tryCatch(
      api.subscription.claimCode(adapter_id, type)
    );

    if (apiResult.error) {
      console.log("Error claiming code: ", apiResult.error.message);
    } else {
      if (apiResult.result.data.result === "Code claimed") {
        window.location.href = "/account?reason=code_claimed";
      } else {
        console.log("Error claiming code: ", apiResult.result.data.result);
      }
    }
  };

  return (
    <div className="py-0 px-8 mx-[-32px]">
      <Title
        title={t("title")}
        subtitle={`${codes.length} ${t("subtitle")}`}
        className="mt-7"
      />
      {codes.length > 0 && (
        <ul className="list-none pl-0">
          <div
            style={{
              display: "flex",
              padding: "20px 0px",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex" }}>
              <div
                style={{
                  width: "45%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("code")}
              </div>
              <div
                style={{
                  width: "25%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("type")}
              </div>
              <div
                style={{
                  width: "30%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("purchasedOn")}
              </div>
            </div>
            {codes.map((code) => (
              <CodeRow code={code} />
            ))}
          </div>
        </ul>
      )}
      <div style={{ display: "flex", padding: "10px 0px", gap: "10px" }}>
        <Button
          data-testid="purchaseNordVPNCode"
          size="small"
          onClick={() => {
            window.location.href = "/account/purchase";
          }}
        >
          {t("purchaseCode")}
        </Button>
        {Object.entries(unclaimedCodes).map(([type, adapters]) => (
          <Button
            data-testid="claimNordVPNCode"
            size="small"
            onClick={() => {
              handleClaimCode(adapters[0], type);
            }}
          >
            {"Claim " +
              type +
              " code (" +
              adapters.length.toString() +
              " remaining)"}
          </Button>
        ))}
      </div>
      <p className="text-sm">
        Please visit{" "}
        <a
          href="https://my.nordaccount.com/activate/"
          target="_blank"
          rel="noreferrer"
          className="group transition text-xs text-gray-500 hover:text-gray-900 border-b border-solid border-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 rounded-sm"
        >
          NordVPN Activation
        </a>{" "}
        to redeem your code. Codes can be used with new or existing accounts.
      </p>
    </div>
  );
};

interface CodeRowProps {
  code: Code;
}

const CodeRow: React.FC<CodeRowProps> = ({ code }) => {
  const { t } = useTranslation("codes");

  return (
    <div
      style={{ display: "flex" }}
      className={"text-gray-500"}
      key={`${code.code}_${code.consumed_on}`}
    >
      <div
        style={{
          width: "45%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">{code.code}</p>
      </div>
      <div
        style={{
          width: "25%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">{code.type}</p>
      </div>
      <div
        style={{
          width: "30%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">
          {new Date(code.consumed_on).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default CodesContainer;
